import React from 'react';
import CodeSnippet from './CodeSnippet';
import {
  DocContainer,
  Title,
  SubTitle,
  Paragraph,
  List,
  ListItem,
  OrderedList,
  OrderedListItem,
  Code,
} from './DocStyles';

const EstilizacaoDoc: React.FC = () => {
  return (
    <DocContainer>
      <Title>Estilização no SmartEntrega</Title>

      <Paragraph>
        Este documento explica como a estilização é implementada no projeto
        SmartEntrega, incluindo o uso de CSS, Styled Components e outras
        técnicas de estilização.
      </Paragraph>

      <SubTitle>Abordagens de Estilização</SubTitle>
      <Paragraph>
        O SmartEntrega utiliza várias abordagens para estilização:
      </Paragraph>
      <OrderedList>
        <OrderedListItem>
          <strong>CSS Tradicional</strong>: Arquivos CSS importados diretamente
        </OrderedListItem>
        <OrderedListItem>
          <strong>Styled Components</strong>: Biblioteca para criar componentes
          estilizados com CSS-in-JS
        </OrderedListItem>
        <OrderedListItem>
          <strong>CSS Modules</strong>: Arquivos CSS com escopo local para
          componentes específicos
        </OrderedListItem>
      </OrderedList>

      <SubTitle>CSS Tradicional</SubTitle>
      <Paragraph>
        Os arquivos CSS tradicionais são usados para estilos globais e reset de
        CSS:
      </Paragraph>
      <CodeSnippet
        code={`/* src/index.css */
body {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

code {
  font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
    monospace;
}

/* Outros estilos globais... */`}
      />

      <Paragraph>
        Estes arquivos são importados diretamente nos componentes React:
      </Paragraph>
      <CodeSnippet code={`import './index.css';`} />

      <SubTitle>Styled Components</SubTitle>
      <Paragraph>
        O Styled Components é a principal abordagem de estilização no
        SmartEntrega. Ele permite criar componentes React com estilos
        encapsulados:
      </Paragraph>

      <SubTitle>Instalação e Configuração</SubTitle>
      <CodeSnippet
        code={`npm install styled-components @types/styled-components
# ou
yarn add styled-components @types/styled-components`}
      />

      <SubTitle>Exemplo Básico</SubTitle>
      <CodeSnippet
        code={`// src/components/Button.tsx
import styled from 'styled-components';

// Criando um botão estilizado
const Button = styled.button\`
  background-color: #007bff;
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  
  &:hover {
    background-color: #0069d9;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
\`;

export default Button;`}
      />

      <SubTitle>Props Dinâmicas</SubTitle>
      <Paragraph>
        Você pode usar props para alterar estilos dinamicamente:
      </Paragraph>
      <CodeSnippet
        code={`// src/components/Button.tsx
import styled from 'styled-components';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'small' | 'medium' | 'large';
}

const Button = styled.button<ButtonProps>\`
  /* Estilos base */
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  
  /* Tamanho baseado na prop size */
  padding: \${props => {
    switch (props.size) {
      case 'small': return '5px 10px';
      case 'large': return '15px 20px';
      default: return '10px 15px';
    }
  }};
  
  font-size: \${props => {
    switch (props.size) {
      case 'small': return '12px';
      case 'large': return '18px';
      default: return '14px';
    }
  }};
  
  /* Cor baseada na prop variant */
  background-color: \${props => {
    switch (props.variant) {
      case 'secondary': return '#6c757d';
      case 'danger': return '#dc3545';
      default: return '#007bff';
    }
  }};
  color: white;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
\`;

export default Button;

// Uso do componente
// <Button variant="primary" size="medium">Clique Aqui</Button>`}
      />

      <SubTitle>Estendendo Componentes</SubTitle>
      <Paragraph>Você pode estender componentes existentes:</Paragraph>
      <CodeSnippet
        code={`// src/components/LinkButton.tsx
import styled from 'styled-components';
import Button from './Button';

const LinkButton = styled(Button)\`
  background-color: transparent;
  border: 1px solid #007bff;
  color: #007bff;
  
  &:hover {
    background-color: #f8f9fa;
  }
\`;

export default LinkButton;`}
      />

      <SubTitle>Temas</SubTitle>
      <Paragraph>
        O Styled Components suporta temas para consistência visual:
      </Paragraph>
      <CodeSnippet
        code={`// src/styles/theme.ts
export const theme = {
  colors: {
    primary: '#007bff',
    secondary: '#6c757d',
    success: '#28a745',
    danger: '#dc3545',
    warning: '#ffc107',
    info: '#17a2b8',
    light: '#f8f9fa',
    dark: '#343a40',
  },
  fonts: {
    body: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    heading: 'inherit',
    monospace: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  fontSizes: {
    small: '12px',
    medium: '14px',
    large: '16px',
    xlarge: '18px',
    xxlarge: '24px',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
  },
  breakpoints: {
    xs: '0px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
  },
};

export type Theme = typeof theme;`}
      />

      <Paragraph>Configurando o provedor de tema:</Paragraph>
      <CodeSnippet
        code={`// src/App.tsx
import React from 'react';
import { ThemeProvider } from 'styled-components';
import { theme } from './styles/theme';
import AppRoutes from './routes';

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <AppRoutes />
    </ThemeProvider>
  );
};

export default App;`}
      />

      <Paragraph>Usando o tema em componentes:</Paragraph>
      <CodeSnippet
        code={`// src/components/Button.tsx
import styled from 'styled-components';
import { Theme } from '../styles/theme';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'danger';
}

const Button = styled.button<ButtonProps>\`
  background-color: \${props => {
    const theme = props.theme as Theme;
    switch (props.variant) {
      case 'secondary': return theme.colors.secondary;
      case 'danger': return theme.colors.danger;
      default: return theme.colors.primary;
    }
  }};
  color: white;
  padding: \${props => (props.theme as Theme).spacing.sm} \${props => (props.theme as Theme).spacing.md};
  border: none;
  border-radius: 4px;
  font-family: \${props => (props.theme as Theme).fonts.body};
  font-size: \${props => (props.theme as Theme).fontSizes.medium};
  
  &:hover {
    opacity: 0.9;
  }
\`;

export default Button;`}
      />

      <SubTitle>Estilos Globais</SubTitle>
      <Paragraph>Para estilos globais com Styled Components:</Paragraph>
      <CodeSnippet
        code={`// src/styles/GlobalStyles.ts
import { createGlobalStyle } from 'styled-components';
import { Theme } from './theme';

const GlobalStyles = createGlobalStyle<{ theme: Theme }>\`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: \${props => props.theme.fonts.body};
    font-size: \${props => props.theme.fontSizes.medium};
    line-height: 1.5;
    color: \${props => props.theme.colors.dark};
    background-color: \${props => props.theme.colors.light};
  }
  
  a {
    color: \${props => props.theme.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  button {
    cursor: pointer;
  }
\`;

export default GlobalStyles;`}
      />

      <Paragraph>Adicionando estilos globais à aplicação:</Paragraph>
      <CodeSnippet
        code={`// src/App.tsx
import React from 'react';
import { ThemeProvider } from 'styled-components';
import { theme } from './styles/theme';
import GlobalStyles from './styles/GlobalStyles';
import AppRoutes from './routes';

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <AppRoutes />
    </ThemeProvider>
  );
};

export default App;`}
      />

      <SubTitle>Layout Responsivo</SubTitle>
      <Paragraph>
        Para criar layouts responsivos com Styled Components:
      </Paragraph>
      <CodeSnippet
        code={`// src/components/Grid.tsx
import styled from 'styled-components';
import { Theme } from '../styles/theme';

// Container com largura máxima e centralizado
export const Container = styled.div\`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
\`;

// Row para sistema de grid
export const Row = styled.div\`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
\`;

// Coluna com tamanho responsivo
interface ColProps {
  xs?: number; // 1-12 (sistema de 12 colunas)
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

export const Col = styled.div<ColProps>\`
  padding: 0 15px;
  width: \${props => props.xs ? \`\${(props.xs / 12) * 100}%\` : '100%'};
  
  @media (min-width: \${props => (props.theme as Theme).breakpoints.sm}) {
    width: \${props => props.sm ? \`\${(props.sm / 12) * 100}%\` : null};
  }
  
  @media (min-width: \${props => (props.theme as Theme).breakpoints.md}) {
    width: \${props => props.md ? \`\${(props.md / 12) * 100}%\` : null};
  }
  
  @media (min-width: \${props => (props.theme as Theme).breakpoints.lg}) {
    width: \${props => props.lg ? \`\${(props.lg / 12) * 100}%\` : null};
  }
  
  @media (min-width: \${props => (props.theme as Theme).breakpoints.xl}) {
    width: \${props => props.xl ? \`\${(props.xl / 12) * 100}%\` : null};
  }
\`;`}
      />

      <Paragraph>Uso do sistema de grid:</Paragraph>
      <CodeSnippet
        code={`// src/pages/Dashboard.tsx
import React from 'react';
import { Container, Row, Col } from '../components/Grid';
import StatisticBox from '../components/StatisticBox';
import Chart from '../components/Chart';

const Dashboard: React.FC = () => {
  return (
    <Container>
      <Row>
        <Col xs={12} md={4}>
          <StatisticBox title="Total de Entregas" value={150} />
        </Col>
        <Col xs={12} md={4}>
          <StatisticBox title="Entregas Pendentes" value={30} />
        </Col>
        <Col xs={12} md={4}>
          <StatisticBox title="Entregas Concluídas" value={120} />
        </Col>
      </Row>
      
      <Row>
        <Col xs={12} lg={6}>
          <Chart type="bar" title="Entregas por Região" />
        </Col>
        <Col xs={12} lg={6}>
          <Chart type="line" title="Desempenho Mensal" />
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;`}
      />

      <SubTitle>Animações</SubTitle>
      <Paragraph>Para adicionar animações com Styled Components:</Paragraph>
      <CodeSnippet
        code={`// src/styles/animations.ts
import { keyframes } from 'styled-components';

export const fadeIn = keyframes\`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
\`;

export const slideIn = keyframes\`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
\`;

export const spin = keyframes\`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
\`;`}
      />

      <Paragraph>Usando animações em componentes:</Paragraph>
      <CodeSnippet
        code={`// src/components/Modal.tsx
import styled from 'styled-components';
import { fadeIn, slideIn } from '../styles/animations';

const ModalOverlay = styled.div\`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  animation: \${fadeIn} 0.3s ease-out;
\`;

const ModalContent = styled.div\`
  background-color: white;
  border-radius: 4px;
  padding: 20px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  animation: \${slideIn} 0.3s ease-out;
\`;

// Componente de loading
const Spinner = styled.div\`
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: \${spin} 1s linear infinite;
\`;`}
      />

      <SubTitle>Conclusão</SubTitle>
      <Paragraph>
        O SmartEntrega utiliza principalmente o Styled Components para
        estilização, o que permite criar componentes React com estilos
        encapsulados e dinâmicos. Esta abordagem facilita a manutenção e a
        reutilização de estilos, além de permitir a criação de temas e estilos
        responsivos.
      </Paragraph>
      <Paragraph>
        Ao seguir as práticas descritas neste documento, você pode criar
        interfaces consistentes e responsivas para o SmartEntrega.
      </Paragraph>
    </DocContainer>
  );
};

export default EstilizacaoDoc;
